import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { ServiceType } from '../types/serviceType';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const ServiceTypeManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState<ServiceType | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch service types with server-side filtering and pagination
  const {
    data: serviceTypesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'serviceTypes',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/serviceType?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('ServiceType API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const serviceTypes = serviceTypesResponse?.data || [];
  const pagination = serviceTypesResponse?.pagination;

  // Create mutation
  const createServiceTypeMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/serviceType', data);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['serviceTypes'] });
      toast({
        title: 'Success',
        description: 'Service type created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create service type';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateServiceTypeMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/serviceType/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceTypes'] });
      toast({
        title: 'Success',
        description: 'Service type updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingServiceType(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update service type';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteServiceTypeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/serviceType/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceTypes'] });
      toast({
        title: 'Success',
        description: 'Service type deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete service type',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration
  const columns: TableColumn<ServiceType>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_, serviceType) => (
        <div className="font-medium">{serviceType.name}</div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (_, serviceType) => (
        <div className="font-mono text-sm">{serviceType.code}</div>
      ),
    },
    {
      key: 'invoiceHead',
      label: 'Invoice Head',
      sortable: false,
      render: (_, serviceType) => (
        <div className="text-sm text-muted-foreground max-w-xs truncate">
          {serviceType.invoiceHead || '-'}
        </div>
      ),
    },
    {
      key: 'gstPercentage',
      label: 'GST %',
      sortable: true,
      render: (_, serviceType) => (
        <div className="text-sm">
          {serviceType.gstPercentage !== undefined && serviceType.gstPercentage !== null 
            ? `${serviceType.gstPercentage}%` 
            : '-'}
        </div>
      ),
    },
    {
      key: 'vatPercentage',
      label: 'VAT %',
      sortable: true,
      render: (_, serviceType) => (
        <div className="text-sm">
          {serviceType.vatPercentage !== undefined && serviceType.vatPercentage !== null
            ? `${serviceType.vatPercentage}%`
            : '-'}
        </div>
      ),
    },
    {
      key: 'sac',
      label: 'SAC',
      sortable: true,
      render: (_, serviceType) => (
        <div className="text-sm font-mono">
          {serviceType.sac || '-'}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, serviceType) => (
        <Badge
          variant={
            serviceType.status === 'Active' ? 'default' : 'secondary'
          }
          className={
            serviceType.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {serviceType.status}
        </Badge>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'Service Type Information',
      description: 'Configure service type details and settings.',
      fields: [
        {
          name: 'name',
          label: 'Service Type Name',
          type: 'text',
          required: true,
          placeholder: 'Enter service type name (e.g., "Air Freight", "Sea Freight")',
          description: 'A descriptive name for this service type',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(100, 'Name cannot exceed 100 characters')
            .regex(
              /^[a-zA-Z0-9\s\-_().]+$/,
              'Name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses'
            ),
        },
        {
          name: 'code',
          label: 'Service Type Code',
          type: 'text',
          required: true,
          placeholder: 'Enter service type code (e.g., "AIR-FRT", "SEA-FRT")',
          description: 'A unique short code for this service type',
          validation: z
            .string()
            .min(2, 'Code must be at least 2 characters')
            .max(20, 'Code cannot exceed 20 characters')
            .regex(
              /^[A-Z0-9_-]+$/,
              'Code can only contain uppercase letters, numbers, underscores and hyphens'
            ),
        },
        {
          name: 'invoiceHead',
          label: 'Invoice Head',
          type: 'textarea',
          required: false,
          placeholder: 'Enter invoice head text (optional)',
          description: 'Text to be displayed on invoices for this service type',
          validation: z
            .string()
            .max(200, 'Invoice head cannot exceed 200 characters')
            .optional(),
        },
        {
          name: 'gstPercentage',
          label: 'GST Percentage',
          type: 'number',
          required: false,
          placeholder: 'Enter GST percentage (e.g., 18)',
          description: 'GST percentage applicable for this service type',
          validation: z.preprocess(
            (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
            z.number()
              .min(0, 'GST percentage cannot be negative')
              .max(100, 'GST percentage cannot exceed 100')
              .optional()
          ),
        },
        {
          name: 'vatPercentage',
          label: 'VAT Percentage',
          type: 'number',
          required: false,
          placeholder: 'Enter VAT percentage (e.g., 5)',
          description: 'VAT percentage applicable for this service type',
          validation: z.preprocess(
            (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
            z.number()
              .min(0, 'VAT percentage cannot be negative')
              .max(100, 'VAT percentage cannot exceed 100')
              .optional()
          ),
        },
        {
          name: 'sac',
          label: 'SAC Number',
          type: 'number',
          required: false,
          placeholder: 'Enter SAC number',
          description: 'Service Accounting Code for this service type',
          validation: z.preprocess(
            (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
            z.number()
              .min(0, 'SAC number cannot be negative')
              .optional()
          ),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: statusOptions,
          description: 'Current status of this service type',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingServiceType(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (serviceType: ServiceType) => {
    setEditingServiceType(serviceType);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (serviceType: ServiceType) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete service type "${serviceType.name}"? This action cannot be undone.`
      )
    ) {
      deleteServiceTypeMutation.mutate(serviceType._id);
    }
  };

  const handleSubmit = async (data: any) => {
    // Transform the data to uppercase code
    const transformedData = {
      ...data,
      code: data.code.toUpperCase(),
    };

    if (editingServiceType) {
      updateServiceTypeMutation.mutate({
        id: editingServiceType._id,
        data: transformedData,
      });
    } else {
      createServiceTypeMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingServiceType(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Service types data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      // Add current filters to export
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/serviceType/export?${params.toString()}`
      );

      if (response.data.success) {
        const serviceTypes = response.data.data;

        // Transform data for export
        const exportData = serviceTypes.map((st: ServiceType) => ({
          Name: st.name,
          Code: st.code,
          'Invoice Head': st.invoiceHead || '',
          'GST Percentage': st.gstPercentage || '',
          'VAT Percentage': st.vatPercentage || '',
          'SAC': st.sac || '',
          Status: st.status,
          'Created At': new Date(st.createdAt).toLocaleString(),
          'Updated At': new Date(st.updatedAt).toLocaleString(),
        }));

        // Convert to CSV
        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map((row: any) =>
            headers
              .map((header) => {
                const value = row[header];
                // Escape values that contain commas or quotes
                if (
                  typeof value === 'string' &&
                  (value.includes(',') || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',')
          ),
        ].join('\n');

        // Download file
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `service-types-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Success',
          description: `Exported ${serviceTypes.length} service types`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export service types',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            Service Type Management
          </h1>
          <p className='text-muted-foreground'>
            Manage service type master data with comprehensive controls
          </p>
        </div>
      </div>

      <MasterDataTable<ServiceType>
        title='Service Types'
        description='Manage and configure service type information'
        data={serviceTypes}
        columns={columns}
        isLoading={isLoading}
        // Pagination
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search service types by name, code, or invoice head...'
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        // Permissions
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingServiceType ? 'Edit Service Type' : 'Create Service Type'}
        sections={formSections}
        initialData={editingServiceType}
        onSubmit={handleSubmit}
        isLoading={
          createServiceTypeMutation.isPending || updateServiceTypeMutation.isPending
        }
        mode={editingServiceType ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default ServiceTypeManagement;

